import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { XdsDialogShellComponent } from 'src/app/shared/ui/dialog-shell/dialog-shell.component';
import { HelpVideo } from '../../config/help-video.config';

/**
 * Spielt ein Erklärvideo aus der Einrichtungs-Checkliste ab.
 *
 * **Bewusst nur ein Rahmen um YouTube.** Der Welcome-Stepper betreibt einen
 * vollständigen IFrame-API-Player mit Fortschrittsmessung und Telemetrie
 * (`registered-stepper.component.ts`) — das braucht er, weil dort das Ansehen
 * ein persistierter Onboarding-Schritt ist. Hier zählt nichts mit, also wäre
 * derselbe Apparat nur Ballast: kein API-Skript, kein Heartbeat, kein Zustand,
 * der schiefgehen kann.
 *
 * `youtube-nocookie.com` statt `youtube.com`: setzt keine Werbe-Cookies, bevor
 * jemand auf Play drückt.
 */
@Component({
  selector: 'app-help-video-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, XdsDialogShellComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <xds-dialog-shell
      [titleKey]="video.titleKey"
      icon="fa-light fa-circle-play"
      size="xl"
      bodyLayout="flush"
      [footerHidden]="true"
      (closed)="ref.close()">
      <div class="help-video">
        <p class="help-video__desc">{{ video.descriptionKey | translate }}</p>
        <div class="help-video__frame">
          <iframe
            [src]="quelle"
            [title]="video.titleKey | translate"
            frameborder="0"
            allow="accelerometer; encrypted-media; picture-in-picture; fullscreen"
            allowfullscreen></iframe>
        </div>
      </div>
    </xds-dialog-shell>
  `,
  styles: [
    `
      .help-video {
        display: flex;
        flex-direction: column;
        /* Untergrenze an die AUSSENBOX: Ein height:100% auf dem inneren
           Container löst im Dialog-Body nicht auf — deshalb bodyLayout="flush"
           und die Höhe hier über das Seitenverhältnis des Videos.
           (Keine Backticks in diesem Block: Er steht in einem
           Template-Literal, ein Backtick beendet es und bricht den Build.) */
        min-height: 0;
        gap: var(--sp-3);
        padding: var(--sp-4);
        box-sizing: border-box;
      }

      .help-video__desc {
        margin: 0;
        font-size: var(--fs-sm);
        line-height: 1.45;
        color: var(--text-secondary);
      }

      .help-video__frame {
        /* Das Video lebt davon, dass man den Dienstplan LIEST. Es bekommt
           deshalb die volle Breite und behält sein 16:9, statt in eine feste
           Höhe gequetscht zu werden. */
        position: relative;
        width: 100%;
        aspect-ratio: 16 / 9;
        min-height: 0;
        border-radius: var(--radius-md, 8px);
        overflow: hidden;
        background: #000;
      }

      .help-video__frame iframe {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }
    `,
  ],
})
export class HelpVideoDialogComponent {
  readonly video: HelpVideo;
  readonly quelle: SafeResourceUrl;

  constructor(
    readonly ref: MatDialogRef<HelpVideoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: { video: HelpVideo },
    sanitizer: DomSanitizer,
  ) {
    this.video = data.video;
    // `cc_load_policy=0`: Die Untertitel stehen bereits im Bild. YouTube würde
    // sonst eine zweite Spur darüberlegen, sobald jemand automatische
    // Untertitel aktiviert hat.
    // `rel=0` hält fremde Videos aus dem Abspann.
    const url =
      `https://www.youtube-nocookie.com/embed/${encodeURIComponent(this.video.videoId)}` +
      '?rel=0&modestbranding=1&cc_load_policy=0';
    this.quelle = sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
